import { GEO5_MODULES, GEO5_PACKAGES } from "../kb/geo5Modules";

function appliquerRegles(data) {
  const modules = new Set();
  const justifications = [];

  const add = (mods, texte, priorite) => {
    mods.forEach(m => { if (GEO5_MODULES[m]) modules.add(m); });
    justifications.push({ modules: mods, texte, priorite });
  };

  const ouv   = data.type_ouvrage || "";
  const niv   = data.nb_niveaux   || 0;
  const charge= data.charge_kN    || 0;
  const fouille = data.fouille_profondeur || 0;
  const zone  = data.zone_sismique || "Non concerné";
  const couches = data.couches || [];
  const zoneActive = ["IIa","IIb","III","IV","V","VI"].includes(zone);
  const zoneFort   = ["III","IV","V","VI"].includes(zone);

  // ── Helpers sol ──────────────────────────────────────────────────
  const aSolMou    = couches.some(c => ["argile","limon"].includes(c.nature) && (c.profondeur_base||0) >= 3);
  const aArgileMolle = couches.some(c => c.nature==="argile" && (c.cohesion||0) < 25 && (c.profondeur_base||0) >= 3);
  const aSable     = couches.some(c => c.nature === "sable");
  const aSableLache = couches.some(c => c.nature==="sable" && (c.N_spt||99) < 15);
  const aRocher    = couches.some(c => c.nature === "roche");
  const aRemblai   = couches.some(c => c.nature==="remblai" && (c.profondeur_base||0) >= 2);
  const aLimon     = couches.some(c => c.nature==="limon");
  const nappeBasse = data.nappe_phreatique && (data.nappe_profondeur||99) < 3;
  const essaisCPT  = (data.essais_disponibles||[]).includes("CPT");

  // ════════════════════════════════════════════════════════════════
  // PROFIL 1 — BÂTIMENT / MAISON / VILLA
  // ════════════════════════════════════════════════════════════════
  if (["batiment","immeuble","maison","villa"].includes(ouv)) {

    // 1a. Sol correct + bâtiment léger → Semelles uniquement
    if (niv <= 4 && !aSolMou && !aArgileMolle) {
      add(["semelles"], `Bâtiment R+${niv} sur sol correct → semelles superficielles.`, 10);
      if (aSolMou || aLimon)
        add(["tassements"], "Sol compressible détecté → vérification tassements nécessaire.", 8);
    }

    // 1b. Sol mou OU bâtiment moyen (R+4 à R+7) → Semelles + Tassements
    if ((niv >= 4 && niv <= 7) || (aSolMou && niv <= 6)) {
      add(["semelles","tassements"], `R+${niv} ou sol compressible → semelles + vérification tassements.`, 10);
      if (aArgileMolle)
        add(["pieux"], "Argile molle (cu<25 kPa) → fondations profondes sur pieux recommandées.", 9);
    }

    // 1c. Bâtiment lourd (R+8+) → Pieux systématiques
    if (niv >= 8) {
      add(["pieux","groupe_pieux","tassements"], `R+${niv} → fondations profondes sur groupe de pieux obligatoires.`, 10);
    }

    // 1d. Charge très élevée + sol mou → Pieux même si peu de niveaux
    if (charge >= 5000 && aSolMou && niv <= 7) {
      add(["pieux","tassements"], `Charge ${charge} kN + sol compressible → pieux nécessaires malgré faible hauteur.`, 9);
    }

    // 1e. Essais CPT disponibles → variantes CPT en remplacement
    if (essaisCPT) {
      if (modules.has("semelles")) add(["semelles_cpt"], "Essais CPT → utiliser GEO5 Semelles CPT (méthode directe, plus précise).", 7);
      if (modules.has("pieux"))    add(["pieux_cpt"],   "Essais CPT → utiliser GEO5 Pieux CPT (méthode LCPC).", 7);
    }

    // 1f. Séisme — seulement si zone active
    if (zoneActive) {
      // MEF sismique uniquement si bâtiment lourd OU site très meuble
      if (niv >= 8 || (zoneFort && aSableLache)) {
        add(["mef_sismique"], `Zone ${zone} + structure lourde → analyse sismique MEF requise (EC8 NF EN 1998-5).`, 9);
      }
      // Liquéfaction si sable lâche N<15
      if (aSableLache) {
        add(["mef_sismique"], `Zone ${zone} + sable lâche N<15 → risque liquéfaction (EC8 NF EN 1998-5 §4.1). Vérification obligatoire.`, 10);
      }
    }

    // 1g. Nappe haute → signaler seulement si fondation superficielle
    if (nappeBasse && modules.has("semelles") && !modules.has("pieux")) {
      add(["semelles"], "Nappe < 3m → vérifier sous-pression et soulèvement hydraulique (onglet Nappe GEO5 Semelles).", 8);
    }
  }

  // ════════════════════════════════════════════════════════════════
  // PROFIL 2 — TALUS / REMBLAI / DÉBLAI / PENTE
  // ════════════════════════════════════════════════════════════════
  else if (["talus","remblai","deblai","pente"].includes(ouv)) {
    add(["stabilite_pentes"], "Tout talus/remblai/déblai nécessite une analyse de stabilité (Bishop min.).", 10);

    // Nappe dans le talus → Écoulement couplé
    if (nappeBasse)
      add(["ecoulement_pentes"], "Nappe < 3m dans le talus → analyse hydraulique couplée (pressions interstitielles).", 8);

    // Roche → Stabilité des roches en plus
    if (aRocher)
      add(["stabilite_roches"], "Présence de roche → analyse cinématique des discontinuités (GEO5 Stabilité des Roches).", 7);

    // Séisme dans talus
    if (zoneActive)
      add(["stabilite_pentes"], `Zone ${zone} → phase sismique obligatoire dans GEO5 Stabilité des Pentes (kh = α·S/r — NF EN 1998-5 §7.3.2.1, SF≥1.10).`, 9);

    // Tassements uniquement si remblai sur sol mou
    if (ouv === "remblai" && aSolMou)
      add(["tassements"], "Remblai sur sol compressible → calcul tassements et consolidation nécessaire.", 8);
  }

  // ════════════════════════════════════════════════════════════════
  // PROFIL 3 — MUR DE SOUTÈNEMENT
  // ════════════════════════════════════════════════════════════════
  else if (["mur","mur_soutenement"].includes(ouv)) {
    if (fouille <= 6) {
      add(["murs_cantilever","pressions_terres"], "Mur H ≤ 6m → GEO5 Murs Cantilever + calcul pressions des terres.", 10);
    } else if (fouille <= 8) {
      // ── RÈGLE MURS CANTILEVER ÉTAGÉS (H 6–8m) ──────────────────────────
      // Calculer la zone d'influence avec φ du sol dominant
      const phi = data.couches?.[0]?.phi || data.frottement || 20;
      const phiRad = phi * Math.PI / 180;
      const L_influence = +(fouille / 2 / Math.tan(Math.PI/4 + phiRad/2)).toFixed(2);
      add(["murs_cantilever","pressions_terres","stabilite_pentes"],
        `⚠️ H=${fouille}m > 6m → Solution recommandée : 2 murs cantilever étagés de ${(fouille/2).toFixed(1)}m chacun. `+
        `Zone d'influence du mur supérieur : L = H/tan(45+φ/2) = ${(fouille/2).toFixed(1)}/tan(45+${phi}/2) = ${L_influence}m. `+
        `Vérifier que la distance D entre les deux murs > ${L_influence}m pour éviter toute interférence.`, 10);
    } else {
      add(["verification_ecrans","conception_ecrans","pressions_terres"],
        `Mur H > 8m → écrans de soutènement (paroi moulée ou berlinoise) ou solution murs étagés avec étude d'interférence.`, 10);
    }

    // Stabilité globale systématique après mur
    add(["stabilite_pentes"], "Après vérification interne du mur → stabilité globale masse sol+mur obligatoire.", 9);

    // Séisme pour les murs
    if (zoneActive)
      add(["murs_cantilever"], `Zone ${zone} → onglet Séisme GEO5 Murs Cantilever obligatoire (Mononobe-Okabe, EC8 NF EN 1998-5).`, 9);

    // Alerte interférence si H > 6m et zone sismique active
    if (fouille > 6 && zoneActive) {
      const phi = data.couches?.[0]?.phi || data.frottement || 20;
      const phiRad = phi * Math.PI / 180;
      const L_influence = +(fouille / 2 / Math.tan(Math.PI/4 + phiRad/2)).toFixed(2);
      add(["murs_cantilever"],
        `Zone sismique ${zone} + H=${fouille}m : si D ≤ ${L_influence}m entre les deux murs → `+
        `ajouter le poids du mur supérieur comme surcharge sur le mur inférieur dans GEO5.`, 8);
    }

    // Sol argileux sous le mur → tassements différentiels
    if (aSolMou)
      add(["tassements"], "Sol compressible sous le mur → tassements différentiels à vérifier (risque inclinaison).", 7);

    // ── RÈGLE GABIONS ──────────────────────────────────────────────
    // Gabions recommandés : H ≤ 5m, pas d'espace contraint, pas Zone V+
    if (ouv !== "deblai" && !["contraint","limite"].includes(data.espaceDisponible) && fouille <= 5 && !["V","VI"].includes(zone)) {
      const avantageNappe = nappeBasse;
      add(["gabions","stabilite_pentes"],
        `H=${fouille}m ≤ 5m + espace disponible → Gabions envisageables : sans ferraillage, esthétique${avantageNappe ? ", drainage naturel (nappe → avantage décisif vs cantilever)" : ""}. Vérifier SF glissement sismique (cas réel Zone IV H=6m : 1.26/1.25 — limite).`,
        avantageNappe ? 10 : 8);
    }
    // Alerte gabions Zone IV+ H > 4m
    if (ouv !== "deblai" && ["IV","V","VI"].includes(zone) && fouille > 4 && fouille <= 6) {
      add(["gabions"],
        `⚠️ Zone ${zone} + H=${fouille}m : gabions SF glissement sismique quasi-limite (cas réel Zone IV H=6m : 1.26/1.25). Augmenter le fruit (décalage 0.50m/bloc) ou réduire H.`, 7);
    }
  }

  // ════════════════════════════════════════════════════════════════
  // PROFIL 4 — EXCAVATION / FOUILLE PROFONDE
  // ════════════════════════════════════════════════════════════════
  else if (ouv === "deblai" || fouille >= 3) {
    if (fouille >= 3 && fouille <= 8) {
      add(["verification_ecrans","conception_ecrans","pressions_terres"], `Fouille ${fouille}m → écrans de soutènement (palplanches ou berlinoise).`, 10);
    } else if (fouille > 8) {
      add(["verification_ecrans","mef"], `Fouille profonde ${fouille}m → écrans + modélisation EF recommandée.`, 10);
    }
    if (nappeBasse)
      add(["verification_ecrans"], "Nappe < 3m → pression hydraulique sur écran. Drainage ou pompage à prévoir.", 8);
    if (zoneActive)
      add(["verification_ecrans"], `Zone ${zone} → analyse sismique de l'écran obligatoire (kh = α·S/r — NF EN 1998-5 §7.3.2.2).`, 9);
  }

  // ════════════════════════════════════════════════════════════════
  // PROFIL 5 — TUNNEL
  // ════════════════════════════════════════════════════════════════
  else if (ouv === "tunnel") {
    add(["mef","mef_tunnel","cuvette_tassement"], "Tunnel → MEF obligatoire (convergence-confinement + tassements surface).", 10);
    if (zoneActive)
      add(["mef_sismique"], `Zone ${zone} → analyse sismique tunnel (MEF dynamique ou pseudo-statique).`, 9);
  }

  // ════════════════════════════════════════════════════════════════
  // PROFIL 6 — INFRASTRUCTURE LINÉAIRE (route, voie ferrée, pipeline)
  // ════════════════════════════════════════════════════════════════
  else if (["route","autoroute","voie_ferree","pipeline"].includes(ouv)) {
    add(["stabilite_pentes","tassements"], "Infrastructure linéaire → stabilité des talus + tassements différentiels.", 9);
    add(["stratigraphie","coupes"], "Infrastructure linéaire → modèle stratigraphique et coupes géologiques le long du tracé.", 7);
    if (zoneActive)
      add(["stabilite_pentes"], `Zone ${zone} → phase sismique obligatoire pour tous les talus (EC8 NF EN 1998-5).`, 9);
  }

  // ════════════════════════════════════════════════════════════════
  // PROFIL 7 — BARRAGE / DIGUE
  // ════════════════════════════════════════════════════════════════
  else if (ouv === "barrage") {
    add(["stabilite_pentes","ecoulement_pentes"], "Barrage en terre → stabilité des 2 talus + analyse de filtration (ligne phréatique).", 10);
    add(["tassements","mef_consolidation"], "Barrage → tassements et consolidation du corps de digue.", 9);
    if (zoneActive)
      add(["mef_sismique"], `Zone ${zone} → analyse sismique du barrage obligatoire (EC8 NF EN 1998-5 + ICOLD).`, 10);
  }

  // ════════════════════════════════════════════════════════════════
  // COUCHE TRANSVERSALE — Campagne de reconnaissance
  // (s'applique à tout type d'ouvrage mais uniquement si cochée)
  // ════════════════════════════════════════════════════════════════
  if (data.campagne_reconnaissance) {
    add(["logs","laboratoire"], "Campagne de reconnaissance → gestion des logs de sondages et essais labo.", 5);
    // Stratigraphie uniquement si infrastructure linéaire ou grand site
    if (["route","autoroute","voie_ferree","barrage"].includes(ouv))
      add(["stratigraphie"], "Infrastructure étendue → modèle stratigraphique 3D depuis les sondages.", 5);
  }

  return { modules: [...modules], justifications: justifications.sort((a,b)=>b.priorite-a.priorite) };
}

function trouverMeilleurPackage(modulesReq, budget, priorite) {
  const resultats = [];
  const prixUnitaire = modulesReq.reduce((s,m) => s + (GEO5_MODULES[m]?.prix || 0), 0);

  for (const [key, pkg] of Object.entries(GEO5_PACKAGES)) {
    const couverts = modulesReq.filter(m => pkg.modules_inclus.includes(m));
    const couverture = couverts.length / Math.max(modulesReq.length, 1);
    const economie = Math.max(0, prixUnitaire - pkg.prix);
    const ratioEco = economie / Math.max(prixUnitaire, 1);

    let score = couverture * 40 + Math.min(ratioEco * 30, 30) + (couverture >= 0.8 ? 30 : 0);
    if (priorite === "securite") score = couverture * 55 + (couverture >= 0.9 ? 45 : 0);
    else if (priorite === "economie") score = couverture * 25 + Math.min(ratioEco * 50, 50) + (pkg.prix < prixUnitaire ? 25 : 0);

    resultats.push({
      key, nom: pkg.nom, ref: pkg.ref, prix: pkg.prix, icone: pkg.icone,
      couverture_pct: Math.round(couverture * 100),
      modules_couverts: couverts,
      modules_manquants: modulesReq.filter(m => !pkg.modules_inclus.includes(m)),
      economie: Math.round(economie),
      prix_unitaire: prixUnitaire,
      score: Math.round(score),
      description: pkg.description
    });
  }

  resultats.push({
    key: "unitaire", nom: "Achat à l'unité", prix: prixUnitaire, icone: "🔧",
    couverture_pct: 100, modules_couverts: modulesReq, modules_manquants: [],
    economie: 0, prix_unitaire: prixUnitaire,
    score: priorite === "economie" ? 55 : 60,
    description: "Uniquement les modules nécessaires pour ce projet."
  });

  resultats.sort((a,b) => b.score - a.score);
  const best = resultats[0];
  const alts = resultats.filter(r => r.key !== best.key && (!budget || r.prix <= budget)).slice(0,3);
  return { recommande: best, alternatives: alts };
}

// ═══════════════════════════════════════════════════════
// COUCHE DE PRÉSÉLECTION — Entonnoir Diagnostic → Mur
// France — EC7 DA2* / NF P94 / EC8 zones 1→5
// ═══════════════════════════════════════════════════════

/**
 * Recommande une solution de soutènement AVANT ouverture de GEO5.
 *
 * @param {number}  H          - Hauteur de soutènement (m)
 * @param {object}  sol        - { nature: string, qa_kPa: number, essais: string[] }
 * @param {string}  espace     - 'libre' | 'contraint' | 'très_contraint'
 * @param {number}  zone_ec8   - Zone sismique EC8 France : 1 à 5
 *
 * @returns {{ solution, label, module_geo5, raisons, alertes,
 *             parametres_a_preparer, prochaine_etape }}
 */
function recommander_solution({ H, sol = {}, espace = 'libre', zone_ec8 = 1 }) {
  const alertes = [];
  const raisons = [];

  const z        = Math.max(1, Math.min(5, Number(zone_ec8) || 1));
  const ag_map   = { 1: 0.040, 2: 0.070, 3: 0.110, 4: 0.160, 5: 0.300 };
  const zone_forte  = z >= 4;
  const zone_active = z >= 2;
  const qa          = Number(sol.qa_kPa) || 200;
  const sol_mou     = qa < 150;
  const sol_drainant = ['sable', 'gravier'].includes(sol.nature);
  const espace_bloque = espace === 'très_contraint';

  let solution = null;
  let module_geo5 = null;
  let prochaine_etape = null;

  // ── Alertes transversales ───────────────────────────────────────
  if (zone_forte && H > 3) {
    alertes.push(
      `Zone EC8 ${z} (ag=${ag_map[z]}g) — Mur Cantilever BA obligatoire : ` +
      `masse d'un mur poids trop élevée sous séisme (F = m × ag). Ductilité BA requise.`
    );
  }
  if (sol_mou) {
    alertes.push(
      `Sol de portance faible (qa ≈ ${qa} kPa < 150 kPa) — ` +
      `vérifier la portance sous semelle AVANT tout dimensionnement du mur. Semelle élargie probable.`
    );
  }
  if (H > 8) {
    alertes.push(
      `H=${H}m > 8m — mur cantilever simple trop flexible. ` +
      `Envisager mur à contreforts ou paroi ancrée (GEO5 Vérification Écrans).`
    );
  }

  // ── Arbre de décision ──────────────────────────────────────────
  if (espace_bloque) {
    solution    = 'paroi_berlinoise';
    module_geo5 = 'verification_ecrans';
    raisons.push('Espace très contraint — impossible de terrasser à l\'arrière pour la semelle talon');
    raisons.push('Paroi berlinoise ou palplanches : emprise nulle côté remblai');
    raisons.push('Module GEO5 Vérification Écrans — Norme EC7 DA2* (France)');

  } else if (H > 8) {
    solution    = 'mur_contreforts';
    module_geo5 = 'murs_cantilever';
    raisons.push(`H=${H}m > 8m — mur cantilever simple en dehors de la plage optimale`);
    raisons.push('Solution 1 : Mur à contreforts BA (contreforts espacés 3–4m)');
    raisons.push('Solution 2 : Deux murs cantilever étagés — distance d\'interférence à vérifier');
    if (espace === 'contraint') {
      raisons.push('Espace contraint + H > 8m → Paroi ancrée recommandée (GEO5 Vérification Écrans)');
    }

  } else if (H >= 3) {
    solution    = 'murs_cantilever';
    module_geo5 = 'murs_cantilever';
    raisons.push(`H=${H}m dans la plage optimale du Mur Cantilever BA (3–8m)`);
    if (sol_mou) {
      raisons.push(`Sol de portance réduite (qa ≈ ${qa} kPa) — semelle élargie (B > 0.7×H) ou fondation sur pieux`);
    } else {
      raisons.push(`Sol de portance correcte (qa ≈ ${qa} kPa) — semelle standard B = 0.5 à 0.7×H suffisante`);
    }
    if (zone_forte) {
      raisons.push(`Zone EC8 ${z} — Cantilever BA préféré : ductilité et chaînage résistent aux forces sismiques horizontales`);
    } else if (zone_active) {
      raisons.push(`Zone EC8 ${z} — Vérification sismique Mononobe-Okabe obligatoire (onglet Séisme GEO5)`);
    }
    if (espace === 'contraint') {
      raisons.push('Espace contraint → optimiser le talon (0.55×B) et le bout de pied (0.15×B) pour réduire l\'emprise');
    }

  } else {
    // H < 3 m
    if (sol_drainant && !zone_forte && espace !== 'contraint') {
      solution    = 'gabions';
      module_geo5 = 'gabions';
      raisons.push(`H=${H}m < 3m — Gabions adaptés : sans ferraillage, auto-drainants, esthétiques`);
      raisons.push('Sol drainant (sable/gravier) → pas de pression interstitielle → avantage gabions');
      raisons.push('Économique : pas de coffrage, pas d\'armatures, mise en œuvre rapide');
    } else if (!zone_forte) {
      solution    = 'murs_poids';
      module_geo5 = 'murs_poids';
      raisons.push(`H=${H}m < 3m — Mur Poids béton cyclopéen adapté : stable par sa masse propre`);
      raisons.push('Pas de ferraillage — économique pour faible hauteur sur sol portant');
      raisons.push('Vérifier portance (charge concentrée en pied) et glissement (EC7 DA2*)');
    } else {
      solution    = 'murs_cantilever';
      module_geo5 = 'murs_cantilever';
      raisons.push(`Zone EC8 ${z} — Mur Cantilever BA même pour H=${H}m : ductilité requise`);
      raisons.push('Mur poids déconseillé zone ≥ 4 : grande masse → forces sismiques élevées sans ductilité');
    }
  }

  // ── Pré-dimensionnement indicatif ─────────────────────────────
  const parametres = [
    { param: 'Hauteur de soutènement H',         valeur: `${H} m`,                    source: 'Plan topographique' },
    { param: 'Angle de frottement φ (remblai)',   valeur: '28–35° (sable), 20–28° (argile)', source: sol.essais?.join(', ') || 'Essai labo ou corrélation' },
    { param: 'Cohésion c (remblai)',              valeur: '0–15 kPa',                  source: 'Essai triaxial' },
    { param: 'Poids volumique γ (remblai)',       valeur: '18–20 kN/m³',               source: 'Essai Proctor ou corrélation' },
    { param: 'Portance admissible qa (fondation)',valeur: `${qa} kPa`,                 source: sol.essais?.includes('PMT') ? 'PMT Ménard (NF P94-261)' : sol.essais?.includes('CPT') ? 'CPT LCPC' : 'Estimation — confirmer par essai' },
    { param: 'Zone sismique EC8 France',         valeur: `Zone ${z} — ag = ${ag_map[z]}g`, source: 'Arrêté 22/10/2010' },
  ];

  if (solution === 'murs_cantilever' || solution === 'mur_contreforts') {
    const B_min = +(H * 0.5).toFixed(1);
    const B_max = +(H * 0.7).toFixed(1);
    const e_tete = Math.ceil((H / 12) * 100);
    const e_pied = Math.ceil((H / 10) * 100);
    parametres.push(
      { param: 'Largeur semelle B (pré-dimensionnement)', valeur: `${B_min} – ${B_max} m  (0.5 à 0.7 × H)`, source: 'NF P94-281 §6' },
      { param: 'Épaisseur voile en tête (minimum)',       valeur: `${e_tete} cm  (H/12)`,                   source: 'EC2 §5.3' },
      { param: 'Épaisseur voile en pied',                 valeur: `${e_pied} cm  (H/10)`,                   source: 'EC2 — moments élevés en pied' },
      { param: 'Position voile sur semelle',              valeur: '1/3 depuis le bord aval (bout de pied)',  source: 'Équilibre des moments — pratique FR' },
    );
  }

  // ── Prochaine étape GEO5 ──────────────────────────────────────
  const etapes_map = {
    murs_cantilever:  'Ouvrir GEO5 Murs Cantilever → Onglet Paramètres → Norme : EC7 DA2* (France)',
    mur_contreforts:  'Ouvrir GEO5 Murs Cantilever (modéliser contrefort) → Onglet Paramètres → EC7 DA2*',
    gabions:          'Ouvrir GEO5 Gabions → Onglet Géométrie → Définir les blocs et angles de fruit',
    murs_poids:       'Ouvrir GEO5 Murs Poids → Onglet Paramètres → Norme : EC7 DA2* (France)',
    paroi_berlinoise: 'Ouvrir GEO5 Vérification Écrans → Onglet Paramètres → Norme EC7 DA2* — Type : Berlinoise',
  };
  prochaine_etape = etapes_map[solution] || 'Ouvrir GEO5 → Choisir le module adapté';

  const labels_map = {
    murs_cantilever:  'Mur Cantilever Béton Armé',
    mur_contreforts:  'Mur à Contreforts BA (ou deux murs étagés)',
    gabions:          'Mur Gabions',
    paroi_berlinoise: 'Paroi Berlinoise / Palplanches',
    murs_poids:       'Mur Poids Béton Cyclopéen',
  };

  return {
    solution,
    label:                labels_map[solution] || solution,
    module_geo5,
    raisons,
    alertes,
    parametres_a_preparer: parametres,
    prochaine_etape,
  };
}

// ═══════════════════════════════════════════════════════
// COUPE GÉOLOGIQUE SVG
// ═══════════════════════════════════════════════════════
const NATURE_STYLES = {
  terre_vegetale: { fill: "#6B4423", stroke: "#4a2f18", label: "Terre végétale", pattern: "veg" },
  remblai:        { fill: "#C8A84B", stroke: "#a08030", label: "Remblai", pattern: "remblai" },
  argile:         { fill: "#7BA3C8", stroke: "#5a82a8", label: "Argile", pattern: "argile" },
  limon:          { fill: "#9BB8A0", stroke: "#708a75", label: "Limon", pattern: "limon" },
  sable:          { fill: "#F0D080", stroke: "#c8a840", label: "Sable", pattern: "sable" },
  gravier:        { fill: "#D4B860", stroke: "#a89040", label: "Gravier", pattern: "gravier" },
  roche:          { fill: "#909090", stroke: "#606060", label: "Roche", pattern: "roche" },
  default:        { fill: "#CCCCCC", stroke: "#999", label: "Sol", pattern: "default" }
};


export { appliquerRegles, trouverMeilleurPackage, recommander_solution };
